import { createSelector } from '@reduxjs/toolkit';
import { moduleNamespace } from '@Store/config.store';
import { NamespacedStoreState } from '@Types/store.types';
import { useSelector } from 'react-redux';
import { getRehabActionsSelector } from '@Selectors/Rehab.selectors';
import { ACTION_FIELDS } from '@Types';
import { initState } from '@Reducers/ChoiceList.reducer';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

const ChoiceListStoreSelector = createSelector(
	parseNamespace,
	state => state?.ChoiceListStore,
);

const FieldOptionsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer => choiceListReducer?.fieldOptions,
);
const ChoiceListIsLoading = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer => choiceListReducer?.isLoading,
);

const RehabRuleChoiceListFilesSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer => choiceListReducer?.allRehabRuleChoiceListFiles,
);

const CombinedRehabRuleChoiceListFilesSelector = createSelector(
	[RehabRuleChoiceListFilesSelector, getRehabActionsSelector],
	(allRehabRuleChoiceListFiles, rehabActions) => {
		if (!allRehabRuleChoiceListFiles) {
			return initState.allRehabRuleChoiceListFiles;
		}

		const actionRecordType =
			rehabActions
				?.map(a => ({
					actionId: a.actionId,
					_id: a._id,
					fields: ACTION_FIELDS,
				}))
				.sort((a, b) =>
					a.actionId.localeCompare(b.actionId, undefined, {
						sensitivity: 'base',
					}),
				) || [];

		return {
			...allRehabRuleChoiceListFiles,
			actionRecordType,
		};
	},
);

const AllRiskConfigsIdsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer =>
		choiceListReducer?.allRehabRuleChoiceListFiles?.riskRecordType?.map(
			riskRecord => {
				return {
					name: riskRecord.name,
					configId: riskRecord.configId,
				};
			},
		),
);

const AllTaskConfigsIdsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer =>
		choiceListReducer?.allRehabRuleChoiceListFiles?.tasksRecordType?.map(
			taskRecord => {
				return {
					name: taskRecord.name,
					configId: taskRecord.uploadId,
				};
			},
		),
);

const AllEventConfigsIdsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer =>
		choiceListReducer?.allRehabRuleChoiceListFiles?.eventsRecordType?.map(
			eventRecord => {
				return {
					name: eventRecord.name,
					configId: eventRecord.uploadId,
				};
			},
		),
);

const AllActionConfigsIdsSelector = createSelector(
	getRehabActionsSelector,
	actionsReducer =>
		actionsReducer?.map(a => {
			return {
				name: a.actionId,
				configId: a._id,
			};
		}),
);

const AllFailureDefinitionConfigsIdsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer =>
		choiceListReducer?.allRehabRuleChoiceListFiles?.failureDefinitionRecordType?.map(
			failureRecord => {
				return {
					name: failureRecord.name,
					configId: failureRecord.configId,
				};
			},
		),
);

const AllDeteriorationModelConfigsIdsSelector = createSelector(
	ChoiceListStoreSelector,
	choiceListReducer =>
		choiceListReducer?.allRehabRuleChoiceListFiles?.deteriorationModelRecordType?.map(
			deteriorationModelRecord => {
				return {
					name: deteriorationModelRecord.name,
					configId: deteriorationModelRecord.configId,
				};
			},
		),
);

export {
	CombinedRehabRuleChoiceListFilesSelector,
	AllRiskConfigsIdsSelector,
	AllTaskConfigsIdsSelector,
	AllEventConfigsIdsSelector,
	AllActionConfigsIdsSelector,
	AllFailureDefinitionConfigsIdsSelector,
	AllDeteriorationModelConfigsIdsSelector,
	FieldOptionsSelector,
	ChoiceListIsLoading,
};

export const selectFieldOptions = () => useSelector(FieldOptionsSelector);

export const selectChoiceListIsLoading = () => useSelector(ChoiceListIsLoading);

export const selectAllRehabRuleChoiceListFiles = () =>
	useSelector(CombinedRehabRuleChoiceListFilesSelector);

export const selectAllRiskConfigsIds = () =>
	useSelector(AllRiskConfigsIdsSelector);

export const selectAllTaskConfigsIds = () =>
	useSelector(AllTaskConfigsIdsSelector);

export const selectAllEventConfigsIds = () =>
	useSelector(AllEventConfigsIdsSelector);
export const selectAllActionConfigsIds = () =>
	useSelector(AllActionConfigsIdsSelector);

export const selectAllFailureDefinitionConfigsIds = () =>
	useSelector(AllFailureDefinitionConfigsIdsSelector);

export const selectAllDeteriorationModelConfigsIds = () =>
	useSelector(AllDeteriorationModelConfigsIdsSelector);
